import { Injectable, OnDestroy, inject } from '@angular/core';
import { TuiDay, TuiTime } from '@taiga-ui/cdk';
import { DateTime } from 'luxon';
import { interval, merge, Observable, Subject } from 'rxjs';
import { filter, finalize, share, startWith, takeUntil } from 'rxjs/operators';

import {
  BrowserTabLifecycleService,
  BrowserTabState,
} from '@common/services/browser-tab-lifecycle.service';
import { transformLuxonDateToTuiDate } from '@common/utils/date-form-transform';
import { dateTimeWithUserTimezone } from '@common/utils/format-date';

/**
 * Сервис для отслеживания смены суток и оповещения подписчиков
 */
@Injectable({
  providedIn: 'root',
})
export class DateChangeService implements OnDestroy {
  private readonly browserTabLifecycleService = inject(BrowserTabLifecycleService);

  /** Subject для отмены подписок при уничтожении сервиса */
  private destroy$ = new Subject<void>();

  /** Subject для оповещения о смене суток */
  private dateChange$ = new Subject<DateTime>();

  /** Текущая дата для сравнения */
  private currentDate: DateTime;

  /** Счетчик активных подписчиков */
  private subscribersCount = 0;

  /** Общий поток для всех подписчиков */
  private sharedDateChange$: Observable<DateTime>;

  constructor() {
    this.currentDate = dateTimeWithUserTimezone();

    this.sharedDateChange$ = this.dateChange$.pipe(
      share(),
      finalize(() => {
        this.subscribersCount--;
        if (this.subscribersCount === 0) {
          this.destroy$.next();
        }
      }),
    );
  }

  /**
   * Инициализирует проверку смены суток
   * @private
   */
  private initializeDateCheck(): void {
    const tabActivation$ = this.browserTabLifecycleService
      .onTabLifecycleChange()
      .pipe(filter((state) => state === BrowserTabState.ACTIVE));

    merge(interval(60000), tabActivation$)
      .pipe(startWith(0), takeUntil(this.destroy$))
      .subscribe(() => {
        const newDate = dateTimeWithUserTimezone();
        if (!newDate.hasSame(this.currentDate, 'day')) {
          this.currentDate = newDate;
          this.dateChange$.next(newDate);
        }
      });
  }

  /**
   * Возвращает текущую дату и время в формате TuiDay с временем конца дня
   * @returns {TuiDay | [TuiDay | null, TuiTime | null] | null} Текущая дата и время в конце дня в часовом поясе пользователя
   */
  public getCurrentDateTime = (): TuiDay | [TuiDay | null, TuiTime | null] | null =>
    transformLuxonDateToTuiDate(dateTimeWithUserTimezone().endOf('day'), {
      withTime: true,
    }) as TuiDay | [TuiDay | null, TuiTime | null] | null;

  /**
   * Возвращает Observable для подписки на события смены суток
   * @returns {Observable<DateTime>} Поток дат при смене суток
   */
  public onDateChange(): Observable<DateTime> {
    this.subscribersCount++;
    if (this.subscribersCount === 1) {
      this.initializeDateCheck();
    }
    return this.sharedDateChange$;
  }

  /**
   * Очищает ресурсы при уничтожении сервиса
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.dateChange$.complete();
  }
}
